import { inject, injectable } from 'inversify';
import { UploadRequestOption } from 'rc-upload/lib/interface';
import { EGlobalDIKeys } from 'core/di/keys';
import type { TSubscriptionFactory } from '../Subscription/types';
import type { IChannels } from '../Channels';
import type { IAWSAPiService } from '../../aws/types';
import type { IRPCApi, IRPCMessages, IRPCService } from './types';
import RPCHandler, { IRPCHandler } from './RPCHandler';

@injectable()
class RPCApi implements IRPCApi {
  constructor(
    @inject(EGlobalDIKeys.RPCMessages) private readonly _messages: IRPCMessages,
    @inject(EGlobalDIKeys.RPCService) private readonly _service: IRPCService,
    @inject(EGlobalDIKeys.Subscription)
    private readonly _subscriptionFactory: TSubscriptionFactory,
    @inject(EGlobalDIKeys.Channels) private readonly _channels: IChannels,
    @inject(EGlobalDIKeys.AWSApiService) private readonly _awsApiService: IAWSAPiService,
  ) {}

  importWinnerTable = async (
    data: UploadRequestOption,
    meta?: any,
  ): Promise<IRPCHandler> => {
    const formData = new FormData();
    data.filename && formData.append(data.filename, data.file);
    const result = await this._awsApiService.upload(formData);

    const subscription = this._createFilesSubscription();
    const sendFn = () => {
      return this._service.send(this._messages.importWinnerTable(meta), result);
    };

    return new RPCHandler(subscription, sendFn);
  };

  importAllDecisions = async (
    data: UploadRequestOption,
    meta?: any,
  ): Promise<IRPCHandler> => {
    const formData = new FormData();
    data.filename && formData.append(data.filename, data.file);
    const result = await this._awsApiService.upload(formData);

    const subscription = this._createFilesSubscription();
    const sendFn = () => {
      return this._service.send(this._messages.importAllDecisions(meta), result);
    };

    return new RPCHandler(subscription, sendFn);
  };

  exportWinnerTable(meta?: any): IRPCHandler {
    const subscription = this._createFilesSubscription();
    const sendFn = () => {
      return this._service.send(this._messages.exportWinnerTable(meta));
    };

    return new RPCHandler(subscription, sendFn);
  }

  exportProposal(data?: any, meta?: any): IRPCHandler {
    const subscription = this._createFilesSubscription();
    const sendFn = () => {
      return this._service.send(this._messages.exportProposal(meta), data);
    };

    return new RPCHandler(subscription, sendFn);
  }

  createInfocard(meta?: any): IRPCHandler {
    const subscription = this._createFilesSubscription();
    const sendFn = () => {
      return this._service.send(this._messages.createInfocard(meta));
    };

    return new RPCHandler(subscription, sendFn);
  }

  createProtocol(meta?: any): IRPCHandler {
    const subscription = this._createFilesSubscription();
    const sendFn = () => {
      return this._service.send(this._messages.createProtocol(meta));
    };

    return new RPCHandler(subscription, sendFn);
  }

  private _createFilesSubscription = () => {
    const subscription = this._subscriptionFactory({
      channel: this._channels.filesChannel(),
    });

    return subscription;
  };

  private _createCompanySubscription = (guid: string) => {
    const subscription = this._subscriptionFactory({
      channel: this._channels.companyChannel(guid),
    });

    return subscription;
  };

  private _createLotSubscription = (guid: string) => {
    const subscription = this._subscriptionFactory({
      channel: this._channels.lotChannel(guid),
    });

    return subscription;
  };

  exportLot = (meta?: any): IRPCHandler => {
    const subscription = this._createFilesSubscription();
    const sendFn = () => {
      return this._service.send(this._messages.exportLot(meta));
    };

    return new RPCHandler(subscription, sendFn);
  };

  importLot = async (data: UploadRequestOption, meta?: any): Promise<IRPCHandler> => {
    const formData = new FormData();
    data.filename && formData.append(data.filename, data.file);
    const result = await this._awsApiService.upload(formData);

    const subscription = this._createFilesSubscription();
    const sendFn = () => {
      return this._service.send(this._messages.importLot(meta), result);
    };

    return new RPCHandler(subscription, sendFn);
  };

  generateLotInfocard = (meta?: any): IRPCHandler => {
    const subscription = this._createFilesSubscription();
    const sendFn = () => {
      return this._service.send(this._messages.generateLotInfocard(meta));
    };

    return new RPCHandler(subscription, sendFn);
  };

  exportProposalInfocard = (meta?: any): IRPCHandler => {
    const subscription = this._createFilesSubscription();
    const sendFn = () => {
      return this._service.send(this._messages.exportProposalInfocard(meta));
    };

    return new RPCHandler(subscription, sendFn);
  };

  importProposal = async (
    data: UploadRequestOption,
    meta?: any,
  ): Promise<IRPCHandler> => {
    const formData = new FormData();
    data.filename && formData.append(data.filename, data.file);
    const result = await this._awsApiService.upload(formData);

    const subscription = this._createFilesSubscription();
    const sendFn = () => {
      return this._service.send(this._messages.importProposal(meta), result);
    };

    return new RPCHandler(subscription, sendFn);
  };

  exportTenderAdditionalCriteria = (meta?: any): IRPCHandler => {
    const subscription = this._createFilesSubscription();
    const sendFn = () => {
      return this._service.send(this._messages.exportTenderAdditionalCriteria(meta));
    };

    return new RPCHandler(subscription, sendFn);
  };

  importTenderAdditionalCriteria = async (
    data: FormData,
    meta?: any,
  ): Promise<IRPCHandler> => {
    const result = await this._awsApiService.upload(data);

    const subscription = this._createFilesSubscription();
    const sendFn = () => {
      return this._service.send(
        this._messages.importTenderAdditionalCriteria(meta),
        result,
      );
    };

    return new RPCHandler(subscription, sendFn);
  };

  exportPrice = (meta?: any): IRPCHandler => {
    const subscription = this._createFilesSubscription();
    const sendFn = () => {
      return this._service.send(this._messages.exportPrice(meta));
    };

    return new RPCHandler(subscription, sendFn);
  };

  importPrice = async (data: UploadRequestOption, meta?: any): Promise<IRPCHandler> => {
    const formData = new FormData();
    data.filename && formData.append(data.filename, data.file);

    const result = await this._awsApiService.upload(formData);

    const subscription = this._createFilesSubscription();
    const sendFn = () => {
      return this._service.send(this._messages.importPrice(meta), result);
    };

    return new RPCHandler(subscription, sendFn);
  };

  exportRatings = (meta?: any): IRPCHandler => {
    const subscription = this._createFilesSubscription();
    const sendFn = () => {
      return this._service.send(this._messages.exportRatings(meta));
    };

    return new RPCHandler(subscription, sendFn);
  };

  exportSecurityFormationConclusion = (meta?: any): IRPCHandler => {
    const subscription = this._createFilesSubscription();
    const sendFn = () => {
      return this._service.send(this._messages.exportSecurityFormationConclusion(meta));
    };

    return new RPCHandler(subscription, sendFn);
  };

  exportAccreditationReport = (meta?: any): IRPCHandler => {
    const subscription = this._createFilesSubscription();
    const sendFn = () => {
      return this._service.send(this._messages.exportAccreditationReport(meta));
    };

    return new RPCHandler(subscription, sendFn);
  };

  companyKonturFocus = (guid: string, meta?: any): IRPCHandler => {
    const subscription = this._createCompanySubscription(guid);
    const sendFn = () => {
      return this._service.send(this._messages.companyKonturFocus(meta));
    };

    return new RPCHandler(subscription, sendFn);
  };

  getBestPrices = (guid: string, meta?: any): IRPCHandler => {
    const subscription = this._createLotSubscription(guid);
    const sendFn = () => {
      return this._service.send(this._messages.lotBestPrices(meta));
    };

    return new RPCHandler(subscription, sendFn);
  };
}

export default RPCApi;
